// @givmo/board — the papers a director reads before a meeting.
//
// ══ THE OPEN IS RECORDED BY THE READ ITSELF — THERE IS NO TRACKING HERE ═════════════════════════
//
// `GET /board-director/…/packets/{id}` writes the engagement receipt server-side, and only AFTER the
// read is allowed: recording an open the director was not permitted would put a false fact in the
// chair's view and turn the receipt table into a disclosure of what was attempted. So this page adds
// nothing — no beacon, no "mark as read", no second call. Fetching the packet IS the receipt, and a
// bespoke tracker beside it could only ever disagree with the one the chair actually reads.
//
// ══ A DRAFT ANSWERS EXACTLY AS A MISSING PACKET DOES ════════════════════════════════════════════
//
// A director reads what the board was SENT. An unissued version is not refused with a hint that it
// exists — it is simply not there, so the error state here says the packet isn't available rather than
// speculating about why.
//
// ══ PRIVATE MEANS PRIVATE, AND THE SCREEN MUST NOT SOFTEN IT ═══════════════════════════════════
//
// `visibility = 'private'` is readable by the authoring SEAT and NOBODY else — not another director,
// and not an administrator of the organization. The filter is applied in SQL and takes no role input, so the
// guarantee is a property of the query rather than something a future author must remember. Two things
// follow for this page:
//
//   * The two lists are RENDERED SEPARATELY and labelled explicitly. A private note appearing anywhere
//     near a heading that says the board can see it is the one failure that makes the feature
//     worthless, so `packets-decode` selects each half POSITIVELY (`=== 'private'` / `=== 'board'`) —
//     a negative test would admit any value a later build adds, in the permissive direction.
//   * There is NO EDIT PATH, deliberately: a board-visible comment other directors have read must not
//     change under them, and a personal note is cheap to replace. So a note is deleted and written
//     again rather than edited in place, and the copy says that instead of hiding it behind a
//     pencil icon that would have to fake it.
//
// An attachment is reachable only because it is IN a version this director may read — never by its own
// id — so downloads are minted through the packet route rather than a document one.

import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams, useSearch } from '../../board-routing';
import {
  isGivmoError,
  type BoardPacketDocumentOut,
  type OrgBoardDirectorPacketOut,
} from '@givmo/api-client';
import {
  Badge,
  Button,
  Card,
  CardBody,
  ErrorMessage,
  Skeleton,
  Textarea,
  testIdProp,
  toast,
} from '@givmo/design-system';
import { BOARD_DIRECTOR_PAGES } from '../../board-routes';
import { useBoardDirectorOperations, useBoardVocab } from '../../board-runtime';
import {
  directorBoardIdentity,
  directorBoardSearch,
  directorOrgOf,
  useDirectorBoardAddress,
} from '../../director/director-board';
import { useDirectorDraft, type DirectorDraftScope } from '../../director/director-drafts';
import { useBoardDirectorHost } from '../../director/director-host';
import {
  DirectorReadRefusal,
  isSigningRefusal,
  useDirectorReadsMayFire,
} from '../../director/read-gate';
import { DirectorBoardContent } from './director-board-content';
import { DirectorReadFailed, useDirectorFailureSentence } from '../../director/access-refusal';
import { DirectorShell } from './director-shell';
import {
  displayInstant,
  displaySize,
  extractAnnotations,
  itemKindLabel,
  itemTitle,
  privateNotes,
  recordedByLabel,
  sharedComments,
  sortPacketItems,
} from '../packets/packets-decode';
import { committeeOnlyWords as co } from '../committees/committee-only-words';

/** CBG-FIX14 — a file that can't be opened because storage is unavailable here: no infrastructure term. */
export const DIRECTOR_FILES_UNAVAILABLE = 'Files can’t be opened right now.';

export function DirectorPacketPage() {
  const { packetId } = useParams({ from: '/director/packets/$packetId' });
  const orgRef = directorOrgOf(useSearch());
  // One packet on one board per mount: when the address names another packet or another board, the
  // page remounts, so what was typed for one is never shown, kept or sent for another, and each
  // comes back with its own drafts (review-43 D2).
  return <DirectorPacket key={`${orgRef ?? ''}:${packetId}`} />;
}

function DirectorPacket() {
  const { packetId } = useParams({ from: '/director/packets/$packetId' });
  const queryClient = useQueryClient();
  const operations = useBoardDirectorOperations();
  const { orgNoun } = useBoardVocab();
  const { orgRef, board, notYours, scope } = useDirectorBoardAddress();
  const mayFire = useDirectorReadsMayFire(orgRef);

  const [noteBody, setNoteBody] = useState('');
  const [commentBody, setCommentBody] = useState('');
  const [error, setError] = useState<string | null>(null);

  const packetKey = ['board-director-packet', orgRef, packetId] as const;
  const annotationsKey = ['board-director-annotations', orgRef, packetId] as const;

  // The seat this account holds on THIS board, from `/me`'s directory. It is a SECOND filter on the
  // private list — the backend's read is already seat-scoped, so a foreign private note cannot arrive;
  // this makes it impossible for one to be rendered under "yours" even if that ever changed.
  const mySeatId = board?.board_member_id ?? null;

  // The note and the comment this account was writing when its session ended (`director-drafts.ts`):
  // kept per account, board, seat and packet, and put back into an empty box once the seat is known.
  const { account } = useBoardDirectorHost();
  const uid = account?.uid;
  const noteScope = useMemo<DirectorDraftScope | null>(
    () =>
      uid !== undefined && orgRef !== null && mySeatId !== null
        ? { uid, orgRef, seatId: mySeatId, surface: 'packet-note', packetId }
        : null,
    [uid, orgRef, mySeatId, packetId],
  );
  const commentScope = useMemo<DirectorDraftScope | null>(
    () =>
      uid !== undefined && orgRef !== null && mySeatId !== null
        ? { uid, orgRef, seatId: mySeatId, surface: 'packet-comment', packetId }
        : null,
    [uid, orgRef, mySeatId, packetId],
  );
  const noteDraft = useDirectorDraft(noteScope, { body: noteBody }, (kept) =>
    setNoteBody((current) => (current.length > 0 ? current : (kept.body ?? ''))),
  );
  const commentDraft = useDirectorDraft(commentScope, { body: commentBody }, (kept) =>
    setCommentBody((current) => (current.length > 0 ? current : (kept.body ?? ''))),
  );

  const packetQuery = useQuery({
    queryKey: packetKey,
    enabled: orgRef !== null && mayFire,
    // A refusal is a deterministic typed 400, not a blip: retrying it just delays the sheet.
    retry: false,
    queryFn: async (): Promise<OrgBoardDirectorPacketOut> => {
      // This call IS the open receipt. Nothing else on this page records one.
      const { data } = await operations.directorGetBoardDirectorPacket({
        path: { org_ref: orgRef ?? '', packet_id: packetId },
        throwOnError: true,
      });
      return data;
    },
  });

  const annotationsQuery = useQuery({
    queryKey: annotationsKey,
    enabled: orgRef !== null && mayFire,
    queryFn: async () => {
      const { data } = await operations.directorListBoardDirectorAnnotations({
        path: { org_ref: orgRef ?? '', packet_id: packetId },
        throwOnError: true,
      });
      return extractAnnotations(data);
    },
  });

  const annotations = annotationsQuery.data ?? [];
  const myNotes = privateNotes(annotations, mySeatId);
  const comments = sharedComments(annotations);

  // CBG-FIX34: a request the board refuses this account re-reads `/me` as it says so.
  const failureSentence = useDirectorFailureSentence();

  const write = useMutation({
    mutationFn: async (input: { body: string; visibility: 'private' | 'board' }) => {
      await operations.directorCreateBoardDirectorAnnotation({
        path: { org_ref: orgRef ?? '', packet_id: packetId },
        body: { body: input.body, visibility: input.visibility },
        throwOnError: true,
      });
    },
    onSuccess: async (_result, input) => {
      setError(null);
      // Sent: the box empties and its draft goes with it.
      if (input.visibility === 'private') {
        noteDraft.discard();
        setNoteBody('');
      } else {
        commentDraft.discard();
        setCommentBody('');
      }
      await queryClient.invalidateQueries({ queryKey: annotationsKey });
      await queryClient.invalidateQueries({ queryKey: packetKey });
      toast.success(
        input.visibility === 'private' ? 'Saved to your notes.' : 'Your comment is on the packet.',
      );
    },
    onError: (caught) => {
      setError(failureSentence(caught, 'We couldn’t save that. Please try again.'));
    },
  });

  const remove = useMutation({
    mutationFn: async (annotationId: string) => {
      await operations.directorDeleteBoardDirectorAnnotation({
        path: { org_ref: orgRef ?? '', packet_id: packetId, annotation_id: annotationId },
        throwOnError: true,
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: annotationsKey });
      await queryClient.invalidateQueries({ queryKey: packetKey });
      toast.success('Deleted.');
    },
    onError: (caught) => {
      toast.error(failureSentence(caught, 'We couldn’t delete that. Please try again.'));
    },
  });

  const download = useMutation({
    mutationFn: async (attachment: BoardPacketDocumentOut) => {
      const { data } = await operations.directorGetBoardDirectorPacketDocumentDownloadUrl({
        path: {
          org_ref: orgRef ?? '',
          packet_id: packetId,
          document_id: attachment.id,
        },
        throwOnError: true,
      });
      return data;
    },
    onSuccess: (link) => {
      window.open(link.download_url, '_blank', 'noopener,noreferrer');
    },
    onError: (caught) => {
      toast.error(
        isGivmoError(caught) && caught.status === 503
          ? DIRECTOR_FILES_UNAVAILABLE
          : failureSentence(caught, 'We couldn’t open that file. Please try again.'),
      );
    },
  });

  const packet = packetQuery.data ?? null;
  const identity = directorBoardIdentity(board);

  if (
    orgRef === null ||
    notYours ||
    packetQuery.isPending ||
    packetQuery.isError ||
    packet === null
  ) {
    return (
      <DirectorShell
        // CBG-YR6-OUTSIDE (review-215's F5): the meeting frame's own "Packet" for a seat for someone
        // who isn't on the board, whose packets are its committees'.
        title={scope.committeeOnly ? 'Packet' : 'Board packet'}
        identity={identity}
        testId="page-director-packet"
      >
        <DirectorBoardContent orgRef={orgRef} notYours={notYours}>
          {packetQuery.isPending ? (
            <Skeleton className="h-90 w-full rounded-lg" testId="director-packet-loading" />
          ) : null}
          {/* ⚠ A refusal must NOT land in the error card below: the read gate opens the sheet and
              re-reads THIS packet once the window is open. */}
          <DirectorReadRefusal reads={[packetQuery]} testId="director-packet-read-step-up" />
          {packetQuery.isError && !isSigningRefusal(packetQuery.error) ? (
            <DirectorReadFailed
              read={packetQuery}
              line="We couldn’t open this packet. It may not have gone out yet."
              testId="director-packet-error"
              retryTestId="director-packet-retry"
            />
          ) : null}
        </DirectorBoardContent>
      </DirectorShell>
    );
  }

  // CBG-FIX90 (frame o10): a committee meeting's packet went to the committee, and its page says so
  // where it said "board" (the packet read names its body, as the meeting and consent reads do).
  const committee =
    typeof packet.committee_name === 'string' && packet.committee_name.length > 0
      ? packet.committee_name
      : null;
  const items = sortPacketItems(packet.items ?? []);
  const attachments = packet.documents ?? [];
  const documentNames = new Map(
    attachments.map((attachment) => [attachment.id, attachment.file_name]),
  );

  return (
    <DirectorShell
      title={committee !== null ? co.packetTitle(committee) : 'Board packet'}
      description={`Version ${packet.version} · sent ${displayInstant(packet.published_at)}`}
      identity={identity}
      actions={
        <Link
          to={BOARD_DIRECTOR_PAGES.meeting}
          params={{ meetingId: packet.meeting_id }}
          search={directorBoardSearch(orgRef)}
          className="font-sans text-s14 font-w600 tracking-givmo text-brand-600 outline-none transition-colors hover:text-brand-700 focus-visible:underline"
          {...testIdProp('director-packet-back')}
        >
          Back to the meeting
        </Link>
      }
      testId="page-director-packet"
    >
      {packet.note ? (
        <Card testId="director-packet-note">
          <CardBody>
            <p className="font-sans text-s15 font-w400 leading-h15 tracking-givmo text-text-secondary">
              {packet.note}
            </p>
          </CardBody>
        </Card>
      ) : null}

      {/* ── Contents ────────────────────────────────────────────────────────────────────────── */}
      <Card testId="director-packet-contents">
        <CardBody className="flex flex-col gap-12">
          <h2 className="font-sans text-s15 font-w600 tracking-givmo text-text-strong">
            What’s in it
          </h2>

          {items.length === 0 ? (
            <p
              className="font-sans text-s15 font-w400 leading-h15 tracking-givmo text-text-secondary"
              {...testIdProp('director-packet-empty')}
            >
              This packet has nothing in it.
            </p>
          ) : (
            <ol className="flex flex-col gap-8" data-testid="director-packet-items">
              {items.map((item, index) => (
                <li
                  key={item.id}
                  className="flex gap-12 rounded-lg border border-border-subtle p-12"
                  data-testid={`director-packet-item-${item.id}`}
                >
                  <span className="w-20 shrink-0 text-center font-sans text-s13 font-w600 tracking-givmo text-text-tertiary">
                    {index + 1}
                  </span>
                  <span className="flex min-w-0 flex-1 flex-col gap-4">
                    <span className="font-sans text-s15 font-w600 tracking-givmo text-text-strong">
                      {itemTitle(item, { documentNames })}
                    </span>
                    {item.body ? (
                      <span className="font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary">
                        {item.body}
                      </span>
                    ) : null}
                    <span className="flex flex-wrap items-center gap-8">
                      <Badge tone="neutral">{itemKindLabel(item.item_kind)}</Badge>
                    </span>
                  </span>
                </li>
              ))}
            </ol>
          )}
        </CardBody>
      </Card>

      {/* ── Files ───────────────────────────────────────────────────────────────────────────── */}
      {attachments.length > 0 ? (
        <Card testId="director-packet-documents">
          <CardBody className="flex flex-col gap-12">
            <h2 className="font-sans text-s15 font-w600 tracking-givmo text-text-strong">Files</h2>
            <ul className="flex flex-col gap-8">
              {attachments.map((attachment) => {
                const size = displaySize(attachment.byte_size);
                return (
                  <li
                    key={attachment.id}
                    className="flex flex-wrap items-center justify-between gap-10 rounded-lg border border-border-subtle p-12"
                    data-testid={`director-packet-document-${attachment.id}`}
                  >
                    <span className="flex min-w-0 flex-col gap-2">
                      <span className="truncate font-sans text-s14 font-w600 tracking-givmo text-text-strong">
                        {attachment.file_name}
                      </span>
                      {size !== null ? (
                        <span className="font-sans text-s12 font-w400 tracking-givmo text-text-tertiary">
                          {size}
                        </span>
                      ) : null}
                    </span>
                    <Button
                      appearance="saas"
                      tone="secondary"
                      size="sm"
                      loading={download.isPending && download.variables?.id === attachment.id}
                      onClick={() => download.mutate(attachment)}
                      testId={`director-packet-open-${attachment.id}`}
                    >
                      Open
                    </Button>
                  </li>
                );
              })}
            </ul>
          </CardBody>
        </Card>
      ) : null}

      {error !== null ? (
        <ErrorMessage testId="director-packet-write-error">{error}</ErrorMessage>
      ) : null}

      {/* ── Private notes ───────────────────────────────────────────────────────────────────── */}
      <Card testId="director-packet-private-notes">
        <CardBody className="flex flex-col gap-12">
          <div className="flex flex-wrap items-center gap-10">
            <h2 className="font-sans text-s15 font-w600 tracking-givmo text-text-strong">
              Your private notes
            </h2>
            <Badge tone="info">Only you can see these</Badge>
          </div>

          <p className="font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-secondary">
            {committee !== null
              ? co.privateNotesLine(orgNoun)
              : `Nobody else on this board can read these, and neither can the ${orgNoun}’s staff. They stay attached to this version of the packet.`}
          </p>

          {myNotes.length > 0 ? (
            <ul className="flex flex-col gap-8" data-testid="director-packet-note-list">
              {myNotes.map((note) => (
                <li
                  key={note.id}
                  className="flex flex-wrap items-start justify-between gap-10 rounded-lg border border-border-subtle p-12"
                  data-testid={`director-packet-note-${note.id}`}
                >
                  <span className="flex min-w-0 flex-1 flex-col gap-2">
                    <span className="font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary">
                      {note.body}
                    </span>
                    <span className="font-sans text-s12 font-w400 tracking-givmo text-text-tertiary">
                      {displayInstant(note.created_at)}
                    </span>
                  </span>
                  <Button
                    appearance="saas"
                    tone="secondary"
                    size="sm"
                    disabled={remove.isPending}
                    onClick={() => remove.mutate(note.id)}
                    testId={`director-packet-note-delete-${note.id}`}
                  >
                    Delete
                  </Button>
                </li>
              ))}
            </ul>
          ) : mySeatId === null ? (
            // The private list is filtered against THIS seat, so until the seat is known the honest
            // statement is "still loading" — "you have no notes" would be a claim we cannot yet make.
            //
            // ⚠ KEPT DELIBERATELY, THOUGH IT IS CURRENTLY UNREACHABLE. The annotations read is held at
            // its `enabled` until `/me` answers, and `/me`'s directory is what supplies this seat. That
            // unreachability is a property of the read gate, not of this component: this branch costs
            // one line and outlives a change to it.
            <Skeleton className="h-32 w-full rounded-lg" testId="director-packet-notes-loading" />
          ) : (
            <p
              className="font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary"
              {...testIdProp('director-packet-notes-empty')}
            >
              You haven’t written any notes on this packet.
            </p>
          )}

          <Textarea
            label="Add a note for yourself"
            value={noteBody}
            onChange={(event) => setNoteBody(event.target.value)}
            rows={3}
            testId="director-packet-note-body"
          />
          <p className="font-sans text-s12 font-w400 leading-h15 tracking-givmo text-text-tertiary">
            A note can’t be edited once saved — delete it and write it again if it changes.
          </p>
          <Button
            appearance="saas"
            tone="primary"
            className="self-start"
            loading={write.isPending && write.variables?.visibility === 'private'}
            disabled={noteBody.trim().length === 0 || write.isPending}
            onClick={() => {
              setError(null);
              write.mutate({ body: noteBody.trim(), visibility: 'private' });
            }}
            testId="director-packet-note-save"
          >
            Save my note
          </Button>
        </CardBody>
      </Card>

      {/* ── Shared comments ─────────────────────────────────────────────────────────────────── */}
      <Card testId="director-packet-comments">
        <CardBody className="flex flex-col gap-12">
          <div className="flex flex-wrap items-center gap-10">
            <h2 className="font-sans text-s15 font-w600 tracking-givmo text-text-strong">
              {committee !== null ? co.commentsTitle : 'Comments for the board'}
            </h2>
            {/* Removed before finishing (frame o10): for a committee's packet, the badge
                "Everyone on the committee sees these" said the title again. */}
            {committee !== null ? null : (
              <Badge tone="neutral">Everyone on this board sees these</Badge>
            )}
          </div>

          {comments.length > 0 ? (
            <ul className="flex flex-col gap-8" data-testid="director-packet-comment-list">
              {comments.map((comment) => {
                const mine = mySeatId !== null && comment.board_member_id === mySeatId;
                const onBehalf = recordedByLabel(comment.recorded_by, { forTheReader: mine });
                return (
                  <li
                    key={comment.id}
                    className="flex flex-wrap items-start justify-between gap-10 rounded-lg border border-border-subtle p-12"
                    data-testid={`director-packet-comment-${comment.id}`}
                  >
                    <span className="flex min-w-0 flex-1 flex-col gap-2">
                      <span className="font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary">
                        {comment.body}
                      </span>
                      <span className="font-sans text-s12 font-w400 tracking-givmo text-text-tertiary">
                        {mine ? 'You' : committee !== null ? co.aCommitteeMember : 'A board member'}{' '}
                        · {displayInstant(comment.created_at)}
                        {onBehalf !== null ? ` · ${onBehalf}` : ''}
                      </span>
                    </span>
                    {mine ? (
                      <Button
                        appearance="saas"
                        tone="secondary"
                        size="sm"
                        disabled={remove.isPending}
                        onClick={() => remove.mutate(comment.id)}
                        testId={`director-packet-comment-delete-${comment.id}`}
                      >
                        Delete
                      </Button>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          ) : (
            <p
              className="font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary"
              {...testIdProp('director-packet-comments-empty')}
            >
              No comments on this packet yet.
            </p>
          )}

          <Textarea
            label={committee !== null ? co.commentLabel : 'Say something to the board'}
            value={commentBody}
            onChange={(event) => setCommentBody(event.target.value)}
            rows={3}
            testId="director-packet-comment-body"
          />
          <Button
            appearance="saas"
            tone="secondary"
            className="self-start"
            loading={write.isPending && write.variables?.visibility === 'board'}
            disabled={commentBody.trim().length === 0 || write.isPending}
            onClick={() => {
              setError(null);
              write.mutate({ body: commentBody.trim(), visibility: 'board' });
            }}
            testId="director-packet-comment-save"
          >
            {committee !== null ? co.postButton : 'Post to the board'}
          </Button>
        </CardBody>
      </Card>
    </DirectorShell>
  );
}
